/**
 * Admin tab — WC 2026 League (iter 76 · Phase B2.1).
 *
 * Unified replacement for the old AdminWCImmersionTab. Lets staff:
 *   • Edit shard cap, advance % per stage, scoring, forfeit window
 *   • Edit fixtures inline (team_a / team_b)
 *   • See the registered-players list per pool
 *   • Run the daily draw for any date
 *   • Preview the player UI as-if-today were any of the 33 match days
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2, Settings as SettingsIcon, Users, Calendar, Shuffle,
  Save, Eye, ListOrdered, Smartphone, Monitor, Trophy, ChevronRight,
  DatabaseZap, RotateCcw, UserX, AlertTriangle, Trash2, EyeOff, PlayCircle,
} from "lucide-react";

const POOLS = ["mobile", "console"];

const FLAG = {
  "Mexico": "🇲🇽", "South Africa": "🇿🇦", "Korea Republic": "🇰🇷", "Czechia": "🇨🇿",
  "Canada": "🇨🇦", "Bosnia & Herzegovina": "🇧🇦", "Qatar": "🇶🇦", "Switzerland": "🇨🇭",
  "Brazil": "🇧🇷", "Morocco": "🇲🇦", "Haiti": "🇭🇹", "Scotland": "🏴󠁧󠁢󠁳󠁣󠁴󠁿",
  "USA": "🇺🇸", "Paraguay": "🇵🇾", "Australia": "🇦🇺", "Türkiye": "🇹🇷",
  "Germany": "🇩🇪", "Curaçao": "🇨🇼", "Côte d'Ivoire": "🇨🇮", "Ecuador": "🇪🇨",
  "Netherlands": "🇳🇱", "Japan": "🇯🇵", "Sweden": "🇸🇪", "Tunisia": "🇹🇳",
  "Belgium": "🇧🇪", "Egypt": "🇪🇬", "Iran": "🇮🇷", "New Zealand": "🇳🇿",
  "Spain": "🇪🇸", "Cabo Verde": "🇨🇻", "Saudi Arabia": "🇸🇦", "Uruguay": "🇺🇾",
  "France": "🇫🇷", "Senegal": "🇸🇳", "Iraq": "🇮🇶", "Norway": "🇳🇴",
  "Argentina": "🇦🇷", "Algeria": "🇩🇿", "Austria": "🇦🇹", "Jordan": "🇯🇴",
  "Portugal": "🇵🇹", "Congo DR": "🇨🇩", "Uzbekistan": "🇺🇿", "Colombia": "🇨🇴",
  "England": "🏴󠁧󠁢󠁥󠁮󠁧󠁿", "Croatia": "🇭🇷", "Ghana": "🇬🇭", "Panama": "🇵🇦",
};
const flag = (t) => FLAG[t] || "🏳️";

export default function AdminWc26LeagueTab() {
  const [pool, setPool] = useState("mobile");
  const [tour, setTour] = useState(null);
  const [err, setErr] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const { data } = await api.get(`/wc26/admin/dashboard?pool=${pool}`);
      setTour(data);
    } catch (e) {
      setErr(formatApiError(e));
      toast.error(formatApiError(e));
    }
  }, [pool]);
  useEffect(() => { load(); }, [load, refreshKey]);

  // iter 96 — surface a retryable error instead of spinning forever when the
  // dashboard endpoint fails (this tab also hosts the cross-cutting WC ops).
  if (err && !tour) {
    return (
      <div className="rounded-xl border border-[#EF4444]/40 bg-[#EF4444]/5 p-4 text-center space-y-3" data-testid="admin-wc26-error">
        <div className="inline-flex items-center gap-2 text-[#EF4444] font-mono text-[12px]">
          <AlertTriangle className="w-4 h-4" /> Couldn’t load the WC26 dashboard
        </div>
        <div className="text-[11px] text-[#71717A] break-words">{err}</div>
        <button
          type="button"
          onClick={reload}
          data-testid="admin-wc26-retry"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#FFD700] text-[#020617] font-mono text-[11px] uppercase tracking-widest"
        >
          <RotateCcw className="w-3 h-3" /> Retry
        </button>
      </div>
    );
  }
  if (!tour) {
    return <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8 text-[#FFD700]" data-testid="admin-wc26-loading" />;
  }

  return (
    <div className="space-y-5" data-testid="admin-wc26-tab">
      {/* Pool selector */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-mono text-[13px] uppercase tracking-[0.25em] inline-flex items-center gap-2 text-[#09090B] dark:text-[#F4F4F5]">
          <Trophy className="w-4 h-4 text-[#FFD700]" /> WC 2026 League — {tour.tournament.name}
        </h2>
        <div className="inline-flex rounded-full bg-[#FAFAFC] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-0.5">
          {POOLS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPool(p)}
              data-testid={`admin-wc26-pool-${p}`}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-widest transition-colors ${
                pool === p
                  ? "bg-[#FFD700] text-[#020617]"
                  : "text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
              }`}
            >
              {p === "mobile" ? <Smartphone className="w-3 h-3" /> : <Monitor className="w-3 h-3" />} {p}
            </button>
          ))}
        </div>
      </div>

      <Counters c={tour.counters} shards={tour.shards} />
      <WcOpsPanel onChanged={reload} />
      <SettingsPanel pool={pool} tournament={tour.tournament} onSaved={reload} />
      <StagesPanel pool={pool} tournament={tour.tournament} onSaved={reload} />
      <DemoPanel pool={pool} onChanged={reload} />
      <FixturesPanel pool={pool} refreshKey={refreshKey} onSaved={reload} />
      <PlayersPanel pool={pool} refreshKey={refreshKey} onChanged={reload} />
    </div>
  );
}

/* ============================================================ */
function Counters({ c, shards }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="admin-wc26-counters">
      <Stat label="Players" value={c.players_total} />
      <Stat label="Alive" value={c.players_alive} accent="#10B981" />
      <Stat label="Matches" value={c.matches_total} />
      <Stat label="Pending" value={c.matches_pending} accent="#FBBF24" />
      <Stat label="Confirmed" value={c.matches_confirmed} accent="#10B981" />
      <Stat label="Disputed" value={c.matches_disputed} accent="#EF4444" />
    </div>
  );
}
function Stat({ label, value, accent = "#FFD700" }) {
  return (
    <div className="rounded-xl border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#1A1A1E] p-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">{label}</div>
      <div className="text-2xl font-extrabold tabular-nums" style={{ color: accent }}>{value}</div>
    </div>
  );
}

/* ============================================================ */
function SettingsPanel({ pool, tournament, onSaved }) {
  const [shardCap, setShardCap] = useState(tournament.shard_cap);
  const [forfeit, setForfeit] = useState(tournament.forfeit_hours);
  const [win, setWin] = useState(tournament.scoring?.win);
  const [draw, setDraw] = useState(tournament.scoring?.draw);
  const [loss, setLoss] = useState(tournament.scoring?.loss);
  const [bye, setBye] = useState(tournament.scoring?.bye);
  const [status, setStatus] = useState(tournament.status);
  const [regOpen, setRegOpen] = useState(tournament.registration_open);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setShardCap(tournament.shard_cap);
    setForfeit(tournament.forfeit_hours);
    setWin(tournament.scoring?.win);
    setDraw(tournament.scoring?.draw);
    setLoss(tournament.scoring?.loss);
    setBye(tournament.scoring?.bye);
    setStatus(tournament.status);
    setRegOpen(tournament.registration_open);
  }, [tournament]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/wc26/admin/settings?pool=${pool}`, {
        shard_cap: shardCap, forfeit_hours: forfeit,
        win_pts: win, draw_pts: draw, loss_pts: loss, bye_pts: bye,
        status, registration_open: regOpen,
      });
      toast.success("Settings saved.");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Section icon={SettingsIcon} title="Settings" testId="admin-wc26-settings">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <NumInput label="Players per shard" value={shardCap} setValue={setShardCap} testId="admin-wc26-shardcap" min={2} max={10000} />
        <NumInput label="Forfeit window (hrs)" value={forfeit} setValue={setForfeit} testId="admin-wc26-forfeit" min={1} max={168} />
        <NumInput label="Win pts" value={win} setValue={setWin} testId="admin-wc26-win" min={0} max={20} />
        <NumInput label="Draw pts" value={draw} setValue={setDraw} testId="admin-wc26-draw" min={0} max={20} />
        <NumInput label="Loss pts" value={loss} setValue={setLoss} testId="admin-wc26-loss" min={0} max={20} />
        <NumInput label="Bye pts" value={bye} setValue={setBye} testId="admin-wc26-bye" min={0} max={20} />
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[#71717A] mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="admin-wc26-status"
            className="w-full bg-white dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] rounded-lg px-2 py-1.5 text-sm"
          >
            {["draft", "registration", "live", "paused", "ended"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[#71717A] mb-1">Registration</label>
          <button
            type="button"
            onClick={() => setRegOpen((v) => !v)}
            data-testid="admin-wc26-regopen"
            className={`w-full rounded-lg px-3 py-1.5 text-sm font-bold border transition-colors ${
              regOpen
                ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-300"
                : "bg-zinc-500/15 border-zinc-500/40 text-zinc-700 dark:text-zinc-300"
            }`}
          >
            {regOpen ? "OPEN" : "CLOSED"}
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={save}
        disabled={busy}
        data-testid="admin-wc26-settings-save"
        className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[#FFD700] text-[#020617] font-bold px-4 py-2 text-sm uppercase tracking-wider disabled:opacity-50"
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} <Save className="w-3.5 h-3.5" /> Save settings
      </button>
    </Section>
  );
}

function NumInput({ label, value, setValue, testId, min, max }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-[#71717A] mb-1">{label}</label>
      <input
        type="number"
        value={value ?? ""}
        min={min}
        max={max}
        onChange={(e) => setValue(parseInt(e.target.value || "0", 10))}
        data-testid={testId}
        className="w-full bg-white dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] rounded-lg px-2 py-1.5 text-sm font-mono tabular-nums"
      />
    </div>
  );
}

/* ============================================================ */
function StagesPanel({ pool, tournament, onSaved }) {
  const [stages, setStages] = useState(tournament.stages || []);
  const [busy, setBusy] = useState(null);
  useEffect(() => setStages(tournament.stages || []), [tournament]);
  const save = async (idx) => {
    setBusy(idx);
    try {
      await api.patch(`/wc26/admin/stages/${idx}?pool=${pool}`, { advance_pct: stages[idx].advance_pct });
      toast.success(`${stages[idx].name} advance % saved.`);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  return (
    <Section icon={ListOrdered} title="Stages & advance %" testId="admin-wc26-stages">
      <div className="space-y-2">
        {stages.map((s, idx) => (
          <div key={s.name + idx} className="flex items-center gap-2 flex-wrap" data-testid={`admin-wc26-stage-${idx}`}>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{s.name}</div>
              <div className="text-[10px] font-mono text-[#71717A]">Days {s.start_day}–{s.end_day} · {s.first_date} → {s.last_date}</div>
            </div>
            <div className="inline-flex items-center gap-1">
              <input
                type="number"
                min={1} max={99}
                value={s.advance_pct ?? ""}
                disabled={s.advance_pct === null}
                onChange={(e) => {
                  const v = parseInt(e.target.value || "0", 10);
                  setStages((cur) => cur.map((x, i) => i === idx ? { ...x, advance_pct: v } : x));
                }}
                data-testid={`admin-wc26-stage-${idx}-pct`}
                className="w-16 bg-white dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] rounded-lg px-2 py-1 text-sm text-center font-mono tabular-nums disabled:opacity-50"
              />
              <span className="text-[10px] font-mono text-[#71717A] mr-1">{s.advance_pct === null ? "n/a (final)" : "% advance"}</span>
              <button
                type="button"
                onClick={() => save(idx)}
                disabled={busy === idx || s.advance_pct === null}
                data-testid={`admin-wc26-stage-${idx}-save`}
                className="inline-flex items-center gap-1 rounded-lg bg-[#FFD700]/15 border border-[#FFD700]/40 text-[#FFD700] text-[10px] font-mono uppercase tracking-widest px-2 py-1 disabled:opacity-30"
              >
                {busy === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
              </button>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ============================================================ */
function DemoPanel({ pool, onChanged }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState("2026-06-11");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const fetchPreview = useCallback(async () => {
    setPreview(null);
    try {
      const { data } = await api.get(`/wc26/admin/preview?as_of=${date}&pool=${pool}`);
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [date, pool]);

  const runDraw = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/wc26/admin/draw`, { pool, date });
      toast.success(`Draw executed: ${(data.runs || []).map((r) => `s${r.shard_num}=${r.status}`).join(", ") || "ok"}`);
      onChanged?.();
      await fetchPreview();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Section icon={Eye} title="Demo mode — simulate any match day" testId="admin-wc26-demo">
      <div className="text-[11px] text-[#71717A] mb-3">
        Pick any of the 33 WC match days, run the daily draw, then preview what a registered player would see today. (Today's actual UTC date is <code className="font-mono">{today}</code>.)
      </div>
      <div className="flex items-end gap-2 flex-wrap">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[#71717A] mb-1">Simulated date</label>
          <input
            type="date"
            min="2026-06-11"
            max="2026-07-19"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="admin-wc26-demo-date"
            className="bg-white dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] rounded-lg px-2 py-1.5 text-sm font-mono"
          />
        </div>
        <button
          type="button"
          onClick={runDraw}
          disabled={busy}
          data-testid="admin-wc26-demo-draw"
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#3B82F6] text-white font-bold px-4 py-2 text-sm uppercase tracking-wider disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shuffle className="w-3.5 h-3.5" />} Run draw
        </button>
        <button
          type="button"
          onClick={fetchPreview}
          data-testid="admin-wc26-demo-preview"
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 text-white font-bold px-4 py-2 text-sm uppercase tracking-wider"
        >
          <Eye className="w-3.5 h-3.5" /> Preview
        </button>
        <a
          href={`/v2/wc2026/league/${pool}?tab=today&as_of=${date}`}
          target="_blank"
          rel="noreferrer"
          data-testid="admin-wc26-demo-open-as-player"
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#FFD700] text-[#020617] font-bold px-4 py-2 text-sm uppercase tracking-wider"
        >
          <ChevronRight className="w-3.5 h-3.5" /> Open as player
        </a>
      </div>

      {preview && (
        <div className="mt-4 rounded-xl border border-[#E4E4E7] dark:border-[#2A2A30] bg-[#FAFAFC] dark:bg-[#0F0F12] p-3" data-testid="admin-wc26-demo-preview-result">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A] mb-1">
            As of {preview.as_of} · Day {preview.today_day ?? "—"} · {preview.today_stage?.name ?? "no stage"}
          </div>
          <div className="text-[12px] mb-2"><strong>Fixtures:</strong></div>
          {(preview.fixtures || []).length === 0 ? (
            <div className="text-[11px] text-[#71717A]">No fixtures seeded for this date.</div>
          ) : (
            <ul className="space-y-1 mb-3">
              {(preview.fixtures || []).map((f) => (
                <li key={f.id} className="flex items-center justify-between rounded-lg bg-white dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] px-2 py-1.5 text-[12px]">
                  <span>{flag(f.team_a)} {f.team_a} <span className="text-[#71717A] mx-1">vs</span> {f.team_b} {flag(f.team_b)}</span>
                  <span className="text-[10px] font-mono text-[#71717A]">{f.group ? `Group ${f.group}` : f.stage}</span>
                </li>
              ))}
            </ul>
          )}
          {preview.today_match ? (
            <div className="rounded-lg bg-[#FFD700]/15 border border-[#FFD700]/40 px-3 py-2 text-[12px]">
              <div className="font-bold mb-0.5">Your match (as admin player):</div>
              <div>{flag(preview.today_match.team_a)} {preview.today_match.team_a} <span className="text-[#71717A]">vs</span> {preview.today_match.team_b} {flag(preview.today_match.team_b)}</div>
              <div className="text-[10px] font-mono text-[#71717A] mt-0.5">vs @{preview.today_match.home_user === preview.me.user_id ? preview.today_match.away_username : preview.today_match.home_username} · {preview.today_match.status}</div>
            </div>
          ) : (
            <div className="text-[11px] text-[#71717A]">No match assigned to you yet. Run draw, then preview.</div>
          )}
        </div>
      )}
    </Section>
  );
}

/* ============================================================ */
// Official 48 WC 2026 nations (name + flag) for the KO fixture dropdowns.
const WC_NATIONS = [
  ["Algeria", "🇩🇿"], ["Argentina", "🇦🇷"], ["Australia", "🇦🇺"], ["Austria", "🇦🇹"],
  ["Belgium", "🇧🇪"], ["Bosnia & Herzegovina", "🇧🇦"], ["Brazil", "🇧🇷"], ["Cabo Verde", "🇨🇻"],
  ["Canada", "🇨🇦"], ["Colombia", "🇨🇴"], ["Congo DR", "🇨🇩"], ["Côte d'Ivoire", "🇨🇮"],
  ["Croatia", "🇭🇷"], ["Curaçao", "🇨🇼"], ["Czechia", "🇨🇿"], ["Ecuador", "🇪🇨"],
  ["Egypt", "🇪🇬"], ["England", "🏴󠁧󠁢󠁥󠁮󠁧󠁿"], ["France", "🇫🇷"], ["Germany", "🇩🇪"],
  ["Ghana", "🇬🇭"], ["Haiti", "🇭🇹"], ["Iran", "🇮🇷"], ["Iraq", "🇮🇶"],
  ["Japan", "🇯🇵"], ["Jordan", "🇯🇴"], ["Korea Republic", "🇰🇷"], ["Mexico", "🇲🇽"],
  ["Morocco", "🇲🇦"], ["Netherlands", "🇳🇱"], ["New Zealand", "🇳🇿"], ["Norway", "🇳🇴"],
  ["Panama", "🇵🇦"], ["Paraguay", "🇵🇾"], ["Portugal", "🇵🇹"], ["Qatar", "🇶🇦"],
  ["Saudi Arabia", "🇸🇦"], ["Scotland", "🏴󠁧󠁢󠁳󠁣󠁴󠁿"], ["Senegal", "🇸🇳"], ["South Africa", "🇿🇦"],
  ["Spain", "🇪🇸"], ["Sweden", "🇸🇪"], ["Switzerland", "🇨🇭"], ["Tunisia", "🇹🇳"],
  ["Türkiye", "🇹🇷"], ["USA", "🇺🇸"], ["Uruguay", "🇺🇾"], ["Uzbekistan", "🇺🇿"],
];

function TeamSelect({ value, onChange, testId }) {
  return (
    <select
      value={value || "TBD"}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full bg-white dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] rounded px-1.5 py-0.5 text-[12px]"
    >
      <option value="TBD">— TBD —</option>
      {WC_NATIONS.map(([name, flag]) => (
        <option key={name} value={name}>{flag} {name}</option>
      ))}
    </select>
  );
}

function FixturesPanel({ pool, refreshKey, onSaved }) {
  const [fixtures, setFixtures] = useState(null);
  const [dirty, setDirty] = useState({});
  const [filterDay, setFilterDay] = useState("all");

  useEffect(() => {
    api.get(`/wc26/schedule?pool=${pool}`)
      .then((r) => setFixtures(r.data.fixtures || []))
      .catch(() => setFixtures([]));
  }, [pool, refreshKey]);

  if (fixtures === null) return <Loader2 className="w-5 h-5 animate-spin mx-auto text-[#FFD700]" />;

  const saveOne = async (f) => {
    const u = dirty[f.id];
    if (!u) return;
    try {
      await api.patch(`/wc26/admin/fixtures/${f.id}`, u);
      toast.success("Fixture saved.");
      setDirty((d) => { const x = { ...d }; delete x[f.id]; return x; });
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setField = (id, k, v) => setDirty((d) => ({ ...d, [id]: { ...(d[id] || {}), [k]: v } }));

  const shown = filterDay === "all" ? fixtures : fixtures.filter((f) => String(f.day) === filterDay);
  const days = Array.from(new Set(fixtures.map((f) => f.day))).sort((a, b) => a - b);

  return (
    <Section icon={Calendar} title={`Fixtures (${fixtures.length})`} testId="admin-wc26-fixtures">
      <div className="flex items-center gap-2 mb-2 overflow-x-auto no-scrollbar">
        <button
          type="button"
          onClick={() => setFilterDay("all")}
          data-testid="admin-wc26-fix-filter-all"
          className={`shrink-0 px-2 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border ${filterDay === "all" ? "bg-[#FFD700] text-[#020617] border-[#FFD700]" : "border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A]"}`}
        >All</button>
        {days.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setFilterDay(String(d))}
            data-testid={`admin-wc26-fix-filter-d${d}`}
            className={`shrink-0 px-2 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border ${filterDay === String(d) ? "bg-[#FFD700] text-[#020617] border-[#FFD700]" : "border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A]"}`}
          >D{d}</button>
        ))}
      </div>
      <div className="max-h-[60vh] overflow-y-auto border border-[#E4E4E7] dark:border-[#2A2A30] rounded-lg">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 bg-[#FAFAFC] dark:bg-[#0F0F12] z-10">
            <tr className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">
              <th className="px-2 py-1.5 text-left">D / Date</th>
              <th className="px-2 py-1.5 text-left">Stage</th>
              <th className="px-2 py-1.5 text-left">Team A</th>
              <th className="px-2 py-1.5 text-left">Team B</th>
              <th className="px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E4E4E7] dark:divide-[#2A2A30]">
            {shown.map((f) => {
              const a = dirty[f.id]?.team_a ?? f.team_a;
              const b = dirty[f.id]?.team_b ?? f.team_b;
              const changed = !!dirty[f.id];
              // Group fixtures are the fixed official draw → free text. Knockout
              // fixtures (R32→Final) are filled by the admin → flag dropdowns.
              const isKO = !f.group;
              return (
                <tr key={f.id} data-testid={`admin-wc26-fix-row-${f.id}`}>
                  <td className="px-2 py-1 font-mono text-[10px] text-[#71717A]">D{f.day} {f.date}</td>
                  <td className="px-2 py-1 uppercase font-mono text-[10px]">{f.group ? `Group ${f.group}` : f.stage}</td>
                  <td className="px-2 py-1">
                    {isKO ? (
                      <TeamSelect value={a} onChange={(v) => setField(f.id, "team_a", v)} testId={`admin-wc26-fix-${f.id}-a`} />
                    ) : (
                      <input
                        type="text" value={a}
                        onChange={(e) => setField(f.id, "team_a", e.target.value)}
                        data-testid={`admin-wc26-fix-${f.id}-a`}
                        className="w-full bg-white dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] rounded px-1.5 py-0.5"
                      />
                    )}
                  </td>
                  <td className="px-2 py-1">
                    {isKO ? (
                      <TeamSelect value={b} onChange={(v) => setField(f.id, "team_b", v)} testId={`admin-wc26-fix-${f.id}-b`} />
                    ) : (
                      <input
                        type="text" value={b}
                        onChange={(e) => setField(f.id, "team_b", e.target.value)}
                        data-testid={`admin-wc26-fix-${f.id}-b`}
                        className="w-full bg-white dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] rounded px-1.5 py-0.5"
                      />
                    )}
                  </td>
                  <td className="px-2 py-1 text-right">
                    <button
                      type="button"
                      onClick={() => saveOne(f)}
                      disabled={!changed}
                      data-testid={`admin-wc26-fix-${f.id}-save`}
                      className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-[#FFD700]/15 border border-[#FFD700]/40 text-[#FFD700] disabled:opacity-30"
                    >Save</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

/* ============================================================ */
function PlayersPanel({ pool, refreshKey, onChanged }) {
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const limit = 50;
  useEffect(() => {
    api.get(`/wc26/admin/players?pool=${pool}&limit=${limit}&offset=${offset}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ total: 0, rows: [] }));
  }, [pool, offset, refreshKey]);

  const removePlayer = async (r) => {
    if (!window.confirm(
      `Remove @${r.username || r.user_id.slice(0, 6)} from the ${pool} Immersion league?\n\n` +
      "Their registration is deleted and any open matches are forfeited."
    )) return;
    setBusyId(r.user_id);
    try {
      await api.post("/admin/wc/kick", { mode: "immersion", pool, user_id: r.user_id });
      toast.success(`Removed @${r.username || r.user_id.slice(0, 6)}`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  if (!data) return <Loader2 className="w-5 h-5 animate-spin mx-auto text-[#FFD700]" />;
  return (
    <Section icon={Users} title={`Registered players (${data.total})`} testId="admin-wc26-players">
      {data.rows.length === 0 ? (
        <div className="text-[11px] text-[#71717A] text-center py-6">No players registered in this pool yet.</div>
      ) : (
        <>
          <div className="max-h-[50vh] overflow-y-auto border border-[#E4E4E7] dark:border-[#2A2A30] rounded-lg">
            <table className="w-full text-[12px]">
              <thead className="sticky top-0 bg-[#FAFAFC] dark:bg-[#0F0F12] z-10">
                <tr className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">
                  <th className="px-2 py-1.5 text-left">#</th>
                  <th className="px-2 py-1.5 text-left">Player</th>
                  <th className="px-2 py-1.5 text-left">Shard</th>
                  <th className="px-2 py-1.5">W/D/L</th>
                  <th className="px-2 py-1.5">GD</th>
                  <th className="px-2 py-1.5 text-right">Pts</th>
                  <th className="px-2 py-1.5">Status</th>
                  <th className="px-2 py-1.5">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E4E4E7] dark:divide-[#2A2A30]">
                {data.rows.map((r, i) => (
                  <tr key={r.id} data-testid={`admin-wc26-player-${r.user_id}`}>
                    <td className="px-2 py-1 font-mono text-[10px] text-[#71717A] tabular-nums">{offset + i + 1}</td>
                    <td className="px-2 py-1 font-bold">@{r.username}</td>
                    <td className="px-2 py-1 font-mono text-[10px]">S{r.shard_num}</td>
                    <td className="px-2 py-1 text-center font-mono text-[10px] tabular-nums">{r.wins}/{r.draws}/{r.losses}</td>
                    <td className="px-2 py-1 text-center font-mono text-[10px] tabular-nums">{r.goal_diff >= 0 ? "+" : ""}{r.goal_diff}</td>
                    <td className="px-2 py-1 text-right font-extrabold tabular-nums text-[#FFD700]">{r.points}</td>
                    <td className="px-2 py-1">
                      <span className={`inline-block px-1.5 py-0.5 rounded-full text-[9px] font-mono uppercase ${
                        r.status === "active" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                        : "bg-rose-500/15 text-rose-700 dark:text-rose-300"
                      }`}>{r.status}</span>
                    </td>
                    <td className="px-2 py-1 text-center">
                      <button
                        type="button"
                        onClick={() => removePlayer(r)}
                        disabled={busyId === r.user_id}
                        data-testid={`admin-wc26-remove-${r.user_id}`}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest bg-rose-500/10 text-rose-600 dark:text-rose-300 hover:bg-rose-500/20 disabled:opacity-40"
                      >
                        {busyId === r.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.total > limit && (
            <div className="flex items-center justify-between mt-2 text-[11px]">
              <button type="button" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - limit))}
                      data-testid="admin-wc26-players-prev" className="font-mono uppercase tracking-widest text-[#71717A] hover:text-[#FFD700] disabled:opacity-30">← Prev</button>
              <span className="font-mono text-[#71717A]">{offset + 1}–{Math.min(offset + limit, data.total)} of {data.total}</span>
              <button type="button" disabled={offset + limit >= data.total} onClick={() => setOffset((o) => o + limit)}
                      data-testid="admin-wc26-players-next" className="font-mono uppercase tracking-widest text-[#71717A] hover:text-[#FFD700] disabled:opacity-30">Next →</button>
            </div>
          )}
        </>
      )}
    </Section>
  );
}

/* ============================================================ */
function Section({ icon: Icon, title, testId, children }) {
  return (
    <div className="rounded-2xl border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#1A1A1E] p-4" data-testid={testId}>
      <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#09090B] dark:text-[#F4F4F5] mb-3 inline-flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-[#FFD700]" /> {title}
      </div>
      {children}
    </div>
  );
}


/* ============================================================ */
/* Cross-cutting WC Ops — Seed Fixtures · Reset Tourney · Kick User
   (Backend: /api/admin/wc/*)
   ============================================================ */
function WcOpsPanel({ onChanged }) {
  return (
    <div
      className="rounded-2xl border border-[#EF4444]/40 bg-gradient-to-br from-[#EF4444]/5 via-transparent to-[#FFD700]/5 p-4 space-y-3"
      data-testid="wc-ops-panel"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#EF4444]">
          WC Ops · Destructive admin actions
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <SeedFixturesCard onChanged={onChanged} />
        <ResetModeCard onChanged={onChanged} />
        <KickUserCard onChanged={onChanged} />
      </div>
      {/* iter 96 — Mode visibility gates now live at the WC26 admin shell top
          level (AdminPanel) so they apply to every sub-tab. */}
      <WcRegistrationsCard />
    </div>
  );
}

export function WcVisibilityCard() {
  const [flags, setFlags] = useState(null);
  const [busy, setBusy] = useState({});
  const MODES = ["tournament", "immersion", "dream-team", "ctf"];
  const LABELS = {
    "tournament": "Authentic Tournament",
    "immersion":  "Immersion League",
    "dream-team": "Dream Team",
    "ctf":        "Collect The Flags",
  };
  const ACCENTS = {
    "tournament": "#FF3B5C",
    "immersion":  "#22D3EE",
    "dream-team": "#34D399",
    "ctf":        "#60A5FA",
  };
  // iter 95 — gates per mode. `page` is the "show the mode UI at all" gate.
  // `registration` controls /join /queue /register. `teams_revealed` is
  // tournament-only. `league_access` is immersion-only.
  const GATES_FOR_MODE = {
    "tournament": ["page", "registration", "teams_revealed"],
    "immersion":  ["page", "registration", "league_access"],
    "dream-team": ["page", "registration"],
    "ctf":        ["page"],   // iter 97 — CTF is single-gate: Page opens it fully
  };
  const GATE_LABELS = {
    page:           "Page",
    registration:   "Reg",
    teams_revealed: "Teams",
    league_access:  "League",
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/wc/visibility/all");
      setFlags(data.flags);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (mode, platform, gate, next) => {
    const k = `${mode}-${platform}-${gate}`;
    setBusy((b) => ({ ...b, [k]: true }));
    try {
      await api.post("/admin/wc/visibility", { mode, platform, gate, visible: next });
      setFlags((f) => ({
        ...f,
        [mode]: {
          ...(f?.[mode] || {}),
          [platform]: { ...(f?.[mode]?.[platform] || {}), [gate]: next },
        },
      }));
      toast.success(`${LABELS[mode]} · ${platform} · ${GATE_LABELS[gate]}: ${next ? "open" : "closed"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((b) => ({ ...b, [k]: false })); }
  };

  if (!flags) {
    return (
      <div className="rounded-xl border border-[#FFD700]/30 bg-white dark:bg-[#1A1A1E] p-3" data-testid="wc-visibility-card-loading">
        <Loader2 className="w-4 h-4 animate-spin mx-auto text-[#FFD700]" />
      </div>
    );
  }

  const renderGateBtn = (mode, platform, gate) => {
    const cell = flags?.[mode]?.[platform] || {};
    // Backwards compat: when `flags` is an old shape (boolean per platform)
    // treat that as the `page` gate.
    const on = typeof cell === "boolean" ? (gate === "page" ? cell : false) : !!cell[gate];
    const k = `${mode}-${platform}-${gate}`;
    return (
      <button
        key={gate}
        type="button"
        onClick={() => toggle(mode, platform, gate, !on)}
        disabled={!!busy[k]}
        data-testid={`wc-gate-toggle-${mode}-${platform}-${gate}`}
        title={`${LABELS[mode]} · ${platform} · ${gate}`}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-mono uppercase tracking-widest border transition ${
          on
            ? "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/40"
            : "bg-[#71717A]/10 text-[#71717A] border-[#71717A]/30"
        }`}
      >
        {busy[k] ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : on ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5" />}
        {GATE_LABELS[gate]}
      </button>
    );
  };

  return (
    <div className="rounded-xl border border-[#FFD700]/30 bg-white dark:bg-[#1A1A1E] p-3 space-y-2" data-testid="wc-visibility-card">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFD700] inline-flex items-center gap-1.5">
          <Eye className="w-3.5 h-3.5" /> Mode Gates (per platform)
        </div>
        <button type="button" onClick={load} className="text-[9px] font-mono text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]">Refresh</button>
      </div>
      <div className="text-[10px] text-[#71717A]">
        <strong>Page</strong> = mode UI visible. <strong>Reg</strong> = accept joins/registrations.
        <strong> Teams</strong> = reveal assigned teams (Tournament). <strong>League</strong> = unlock match draws (Immersion).
      </div>
      <table className="w-full text-[11px] font-mono mt-1">
        <thead>
          <tr className="text-[9px] uppercase tracking-widest text-[#71717A]">
            <th className="text-left p-1">Mode</th>
            <th className="text-center p-1">Mobile</th>
            <th className="text-center p-1">Console</th>
          </tr>
        </thead>
        <tbody>
          {MODES.map((m) => (
            <tr key={m} className="border-t border-[#E4E4E7] dark:border-[#2A2A30]">
              <td className="p-1.5 align-top">
                <span className="font-semibold text-[#09090B] dark:text-[#F4F4F5]" style={{ color: ACCENTS[m] }}>{LABELS[m]}</span>
              </td>
              {["mobile", "console"].map((p) => (
                <td key={p} className="p-1.5 text-center">
                  <div className="inline-flex flex-wrap gap-1 justify-center">
                    {GATES_FOR_MODE[m].map((g) => renderGateBtn(m, p, g))}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <TournamentStartCard />
    </div>
  );
}

function TournamentStartCard() {
  const [busy, setBusy] = useState({});
  const start = async (platform) => {
    if (!window.confirm(`Start Authentic Tournament group stage for ${platform}? This will create all group fixtures NOW (admin override). A fresh tournament will open after.`)) return;
    setBusy((b) => ({ ...b, [platform]: true }));
    try {
      const { data } = await api.post(`/wctourney/admin/${platform}/start`);
      toast.success(`Tournament started — ${data.tournament?.filled || 0} players seeded.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((b) => ({ ...b, [platform]: false })); }
  };
  return (
    <div className="mt-3 rounded-lg border border-[#FF3B5C]/30 bg-[#FF3B5C]/5 p-2.5 space-y-1.5" data-testid="tournament-start-card">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF3B5C]">Start Authentic Tournament (admin)</div>
      <div className="text-[10px] text-[#71717A]">Forces the currently `forming` tournament to lock + begin the group stage immediately. Use after closing registration.</div>
      <div className="flex gap-2">
        {["mobile", "console"].map((p) => (
          <button
            key={p}
            type="button"
            disabled={!!busy[p]}
            onClick={() => start(p)}
            data-testid={`tournament-start-${p}`}
            className="px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-widest border border-[#FF3B5C]/40 text-[#FF3B5C] hover:bg-[#FF3B5C]/10 transition disabled:opacity-50"
          >
            {busy[p] ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <PlayCircle className="w-3 h-3 inline mr-1" />}
            Start {p}
          </button>
        ))}
      </div>
    </div>
  );
}

function WcRegistrationsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showList, setShowList] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/wc/registrations?limit=200");
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="rounded-xl border border-[#FFD700]/30 bg-white dark:bg-[#1A1A1E] p-3" data-testid="wc-registrations-card-loading">
        <Loader2 className="w-4 h-4 animate-spin mx-auto text-[#FFD700]" />
      </div>
    );
  }

  const counts = data.counts || {};
  const labels = data.labels || {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="rounded-xl border border-[#FFD700]/30 bg-white dark:bg-[#1A1A1E] p-3 space-y-2" data-testid="wc-registrations-card">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFD700] inline-flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" /> Pre-launch Registrations
        </div>
        <button
          type="button"
          onClick={load}
          className="text-[9px] font-mono text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
        >
          Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {Object.keys(counts).map((m) => (
          <div key={m} data-testid={`wc-registrations-count-${m}`} className="rounded-md bg-[#FAFAFC] dark:bg-[#0F0F12] border border-[#E4E4E7] dark:border-[#2A2A30] px-2 py-1.5">
            <div className="text-[9px] font-mono uppercase tracking-widest text-[#71717A]">{labels[m] || m}</div>
            <div className="text-xl font-extrabold tabular-nums text-[#FFD700]">{counts[m]}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between text-[10px] font-mono text-[#71717A]">
        <div data-testid="wc-registrations-total">Total: <span className="font-bold text-[#09090B] dark:text-[#F4F4F5]">{total}</span></div>
        <button
          type="button"
          onClick={() => setShowList((v) => !v)}
          data-testid="wc-registrations-toggle-list"
          className="text-[#FFD700] hover:underline"
        >
          {showList ? "Hide" : `View ${data.total} signups →`}
        </button>
      </div>
      {showList && (
        <div className="max-h-64 overflow-y-auto rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] divide-y divide-[#E4E4E7] dark:divide-[#2A2A30]" data-testid="wc-registrations-list">
          {(data.rows || []).map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 px-2 py-1.5 text-[10px] font-mono">
              <div className="font-bold text-[#09090B] dark:text-[#F4F4F5]">@{r.username}</div>
              <div className="text-[#71717A]">{labels[r.mode] || r.mode}</div>
              <div className="text-[#71717A] tabular-nums">{r.registered_at ? new Date(r.registered_at).toLocaleString() : "—"}</div>
            </div>
          ))}
          {(data.rows || []).length === 0 && (
            <div className="text-center py-3 text-[10px] text-[#71717A]">No registrations yet.</div>
          )}
        </div>
      )}
    </div>
  );
}

function SeedFixturesCard({ onChanged }) {
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const run = async () => {
    if (!window.confirm(
      "Re-seed the official FIFA 2026 schedule for BOTH pools?\n\n" +
      "This will WIPE every existing fixture row and replace it with the\n" +
      "104 official fixtures (72 group + 32 KO placeholders) per pool.\n\n" +
      "This is idempotent and safe to run multiple times."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wc/seed-official-fixtures");
      setLast(data.results);
      toast.success("Official fixtures seeded for both pools");
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-[#FFD700]/30 bg-white dark:bg-[#1A1A1E] p-3 space-y-2" data-testid="wc-ops-seed-card">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFD700] inline-flex items-center gap-1.5">
        <DatabaseZap className="w-3.5 h-3.5" /> Seed Official Fixtures
      </div>
      <div className="text-[11px] text-[#71717A] leading-relaxed">
        Re-seed <code className="font-mono">wc26_fixtures</code> with the official FIFA 2026
        schedule (104 matches: 72 group + 32 KO TBD) for both pools.
      </div>
      <button
        type="button"
        onClick={run}
        disabled={busy}
        data-testid="wc-ops-seed-btn"
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#FFD700] text-[#020617] font-mono text-[11px] uppercase tracking-wider px-3 py-2 hover:bg-[#EAB308] disabled:opacity-50 transition-colors"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DatabaseZap className="w-3.5 h-3.5" />}
        {busy ? "Seeding…" : "Run Seed"}
      </button>
      {last && (
        <div className="space-y-1 pt-1" data-testid="wc-ops-seed-result">
          {last.map((r, i) => (
            <div key={i} className="text-[10px] font-mono text-[#71717A]">
              <span className="text-[#FFD700] uppercase">{r.pool}</span>:{" "}
              {r.skipped
                ? <span className="text-[#EF4444]">skipped — {r.skipped}</span>
                : r.error
                  ? <span className="text-[#EF4444]">error — {r.error}</span>
                  : <>deleted {r.deleted} · inserted {r.inserted} ({r.group_stage}G + {r.knockout}KO)</>
              }
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const RESET_MODES = [
  { id: "tournament", label: "48-Team Tournament" },
  { id: "immersion",  label: "Immersion League" },
  { id: "dream-team", label: "Dream Team" },
  { id: "ctf",        label: "Collect The Flags" },
  { id: "all",        label: "ALL modes" },
];

function ResetModeCard({ onChanged }) {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("tournament");
  const [last, setLast] = useState(null);

  const run = async () => {
    const label = (RESET_MODES.find((m) => m.id === mode) || {}).label || mode;
    if (!window.confirm(
      `Reset ${label} for BOTH pools/platforms?\n\n` +
      "This wipes the test data for the selected mode(s):\n" +
      "• Tournament → archives tournaments + deletes matches\n" +
      "• Immersion → deletes players/matches/shards/chats, reopens registration\n" +
      "• Dream Team → deletes squads/players/matches/invites/reports\n\n" +
      "Schedules (fixtures) and cycle quotas are KEPT. This cannot be undone."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wc/reset", { mode });
      setLast(data.results || []);
      toast.success(`Reset complete — ${label}`);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-[#FFD700]/30 bg-white dark:bg-[#1A1A1E] p-3 space-y-2" data-testid="wc-ops-reset-card">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFD700] inline-flex items-center gap-1.5">
        <RotateCcw className="w-3.5 h-3.5" /> Reset Mode (post-test)
      </div>
      <div className="text-[11px] text-[#71717A] leading-relaxed">
        Wipe a mode's test data for both pools. Keeps schedules + quotas; next
        join/register starts fresh.
      </div>
      <select
        value={mode}
        onChange={(e) => setMode(e.target.value)}
        data-testid="wc-ops-reset-mode"
        className="w-full rounded-lg bg-[#F4F4F5] dark:bg-[#0F0F12] border border-[#E4E4E7] dark:border-[#2A2A30] px-2 py-1.5 text-[11px]"
      >
        {RESET_MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
      </select>
      <button
        type="button"
        onClick={run}
        disabled={busy}
        data-testid="wc-ops-reset-btn"
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#F97316] text-white font-mono text-[11px] uppercase tracking-wider px-3 py-2 hover:bg-[#EA580C] disabled:opacity-50 transition-colors"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
        {busy ? "Resetting…" : "Reset Now"}
      </button>
      {last && (
        <div className="space-y-1 pt-1" data-testid="wc-ops-reset-result">
          {last.map((r, i) => (
            <div key={i} className="text-[10px] font-mono text-[#71717A]">
              <span className="text-[#F97316] uppercase">{r.kind}·{r.pool || r.platform}</span>:{" "}
              {r.skipped
                ? <span className="text-[#EF4444]">skipped — {r.skipped}</span>
                : r.error
                  ? <span className="text-[#EF4444]">error — {r.error}</span>
                  : r.kind === "tournament"
                    ? <>archived {r.archived_count} · −{r.deleted_matches} matches</>
                    : r.kind === "immersion"
                      ? <>−{r.players_deleted}P −{r.matches_deleted}M −{r.shards_deleted}S</>
                      : <>−{r.players_deleted}P −{r.matches_deleted}M −{r.squads_deleted}sq</>
              }
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KickUserCard({ onChanged }) {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("immersion");
  const [userId, setUserId] = useState("");
  const [pool, setPool] = useState("mobile");
  const [reason, setReason] = useState("");
  const [last, setLast] = useState(null);

  const run = async () => {
    if (!userId.trim()) { toast.error("Enter a user id"); return; }
    if (!window.confirm(
      `Remove user ${userId} from ${mode} (${pool})?\n\n` +
      `• immersion: drops player, shard slot, forfeits open matches.\n` +
      `• tourney: drops participant from latest open tournament + forfeits matches.\n` +
      `• dt: removes dt_players row for latest cycle + flags matches disputed.`
    )) return;
    setBusy(true);
    try {
      const body = { mode, user_id: userId.trim(), reason: reason.trim() || null };
      if (mode === "tourney") body.platform = pool;
      else body.pool = pool;
      const { data } = await api.post("/admin/wc/kick", body);
      setLast(data);
      if (data.skipped) toast.message(`Skipped — ${data.skipped}`);
      else toast.success(`User removed from ${mode}`);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-[#FFD700]/30 bg-white dark:bg-[#1A1A1E] p-3 space-y-2" data-testid="wc-ops-kick-card">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFD700] inline-flex items-center gap-1.5">
        <UserX className="w-3.5 h-3.5" /> Remove User from WC Mode
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          data-testid="wc-ops-kick-mode"
          className="rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#0F0F12] px-2 py-1.5 text-[11px] font-mono"
        >
          <option value="immersion">Immersion</option>
          <option value="tourney">Tournament</option>
          <option value="dt">Dream Team</option>
        </select>
        <select
          value={pool}
          onChange={(e) => setPool(e.target.value)}
          data-testid="wc-ops-kick-pool"
          className="rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#0F0F12] px-2 py-1.5 text-[11px] font-mono"
        >
          <option value="mobile">Mobile</option>
          <option value="console">Console</option>
        </select>
      </div>
      <input
        type="text"
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
        placeholder="user id (uuid)"
        data-testid="wc-ops-kick-userid"
        className="w-full rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#0F0F12] px-2 py-1.5 text-[11px] font-mono"
      />
      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="reason (optional, max 400)"
        data-testid="wc-ops-kick-reason"
        className="w-full rounded-md border border-[#E4E4E7] dark:border-[#2A2A30] bg-white dark:bg-[#0F0F12] px-2 py-1.5 text-[11px] font-mono"
        maxLength={400}
      />
      <button
        type="button"
        onClick={run}
        disabled={busy}
        data-testid="wc-ops-kick-btn"
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#EF4444] text-white font-mono text-[11px] uppercase tracking-wider px-3 py-2 hover:bg-[#DC2626] disabled:opacity-50 transition-colors"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserX className="w-3.5 h-3.5" />}
        {busy ? "Removing…" : "Remove"}
      </button>
      {last && (
        <div className="pt-1 text-[10px] font-mono text-[#71717A]" data-testid="wc-ops-kick-result">
          {last.skipped
            ? <span className="text-[#FBBF24]">skipped — {last.skipped}</span>
            : <span className="text-[#10B981]">✓ removed from {last.mode}</span>
          }
        </div>
      )}
    </div>
  );
}
